import { createElement as rc, useEffect, useMemo } from 'react';
import lodash from 'lodash';
import useFormControl from '../../hooks/useFormControl';
import useEventSink from '../../hooks/useEventSink';
import { eventSink } from 'lib_ui-services';
import { MultiSelectContext, ListSelectionPolicyContext } from '../contextProviders/MultiSelectBoundary';
import { EventBoundaryContext } from '../contextProviders/EventBoundary';

const { get, pick } = lodash;
export const _private = { useFormControl };
const EMPTY_ARRAY = [];

export function getSelectionRowId(row, keys = EMPTY_ARRAY) {
    return keys.length ? JSON.stringify(keys.map(key => get(row, key))) : row._id;
}

// Selecting all saves the current eligible rows. Later arrivals stay unchecked.
// Optional keys restrict the saved payload to stable identifiers, rather than
// copying display fields such as status into an approval or execution input.
export default function FormListSelection(props) {
    const { hNode } = props;
    const { value, setValue, disabled } = _private.useFormControl({
        ...props,
        hNode: {
            ...hNode,
            id: `${hNode.id}-selection`,
            propertyName: hNode.selectionProperty,
            propertyOverride: undefined
        }
    });
    const { value: enabledValue } = _private.useFormControl({
        ...props,
        hNode: {
            ...hNode,
            id: `${hNode.id}-selection-enabled`,
            propertyName: hNode.selectionEnabledProperty || hNode.propertyName,
            propertyOverride: undefined,
            readOnly: true
        }
    });
    const keys = hNode.selectionKeys || EMPTY_ARRAY;
    const { records, rows } = props;
    const selected = Array.isArray(value) ? value : EMPTY_ARRAY;
    const locked = disabled || (hNode.selectionEnabledProperty && String(enabledValue) !== hNode.selectionEnabledValue);
    const policy = useMemo(() => {
        const eligible = row =>
            !locked &&
            keys.every(key => get(row, key) != null) &&
            (!hNode.selectableProperty || String(get(row, hNode.selectableProperty)) === hNode.selectableValue);
        const eligibleRows = records.filter(eligible);
        const selectedCount = eligibleRows.filter(row =>
            selected.some(item => getSelectionRowId(item, keys) === getSelectionRowId(row, keys))
        ).length;
        const hiddenIds = new Set(
            rows.filter(row => eligible(row) && !records.includes(row)).map(row => getSelectionRowId(row, keys))
        );
        return {
            hiddenSelections: selected.filter(row => hiddenIds.has(getSelectionRowId(row, keys))),
            headerSelection: selectedCount === 0 ? false : selectedCount === eligibleRows.length ? true : null,
            namespace: hNode.namespace,
            relation: hNode.relation,
            canSelect: id =>
                id == null
                    ? records.some(eligible)
                    : records.some(row => getSelectionRowId(row, keys) === id && eligible(row))
        };
    }, [
        locked,
        keys,
        records,
        rows,
        selected,
        hNode.namespace,
        hNode.relation,
        hNode.selectableProperty,
        hNode.selectableValue
    ]);
    const parentSink = useEventSink();
    const boundary = useMemo(
        () => ({
            name: `FormListSelection-${hNode.id}`,
            subscriptions: {},
            subscriptionFilter: { verb: 'select' },
            parentSink
        }),
        [hNode.id, parentSink]
    );
    const [subscribe] = eventSink(boundary);
    useEffect(
        () =>
            subscribe({ verb: 'select', namespace: hNode.namespace, relation: hNode.relation }, ({ id, value }) => {
                if (locked) return;
                const reference = row => (keys.length ? pick(row, keys) : row);
                if (id === '__invertSelection') {
                    // Keep only eligible rows hidden by the filter. Visible ineligible
                    // rows and references no longer in the list must not stay selected.
                    const remaining = policy.hiddenSelections;
                    return setValue(
                        value
                            ? [
                                  ...remaining,
                                  ...records.filter(row => policy.canSelect(getSelectionRowId(row, keys))).map(reference)
                              ]
                            : remaining
                    );
                }
                if (!policy.canSelect(id)) return;
                const row = records.find(record => getSelectionRowId(record, keys) === id);
                const remaining = selected.filter(record => getSelectionRowId(record, keys) !== id);
                setValue(value ? [...remaining, reference(row)] : remaining);
            }),
        [subscribe, hNode.namespace, hNode.relation, locked, keys, policy, records, selected, setValue]
    );
    const selection = Object.fromEntries(selected.map(row => [getSelectionRowId(row, keys), true]));
    return rc(
        ListSelectionPolicyContext.Provider,
        { value: policy },
        rc(
            MultiSelectContext.Provider,
            { value: { [hNode.namespace]: { [hNode.relation]: selection } } },
            rc(EventBoundaryContext.Provider, { value: boundary }, props.children)
        )
    );
}
